import {useState} from 'react'
import {
  Flex,
  Button,
  PopoverTrigger,
  PopoverContent,
  PopoverRoot,
  Portal,
  PopoverPositioner,
  Text,
} from '@chakra-ui/react'
import {LuX, LuCalendar} from 'react-icons/lu'
import {useTranslate} from '@tolgee/react'
import {DatePickerSelector} from './date-picker-selector.js'
import {ListSearchInput, FilterListbox} from './list-elements.js'
import type {GenericFilter, GenericFilterOptions, OperatorOption, Operator, OptionIcons} from './schemas.js'
import {getCriterionIcon, getOptionIcon} from './utils.js'
import {Tolgee} from '../../../integrations/tolgee/index.js'

export interface MultiFilterChipProps {
  currentFilters: GenericFilter[]
  filter: GenericFilter
  index: number
  filterOptions: GenericFilterOptions
  operators: Record<'regular' | 'date', OperatorOption[]>
  criteriaTypes: Record<string, 'regular' | 'date'>
  onChange: (filters: GenericFilter[]) => void
  optionIcons?: OptionIcons
}

type ActivePopover = 'criterion' | 'operator' | 'value' | 'startDate' | 'endDate' | null

export function MultiFilterChip({
  currentFilters,
  filter,
  index,
  filterOptions,
  operators,
  onChange,
  criteriaTypes,
  optionIcons,
}: MultiFilterChipProps) {
  if (!filterOptions) return null

  const {t} = useTranslate('common')

  const [activePopover, setActivePopover] = useState<ActivePopover>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const isValueSelectorDisabled = filter.operator === 'IS EMPTY' || filter.operator === 'IS NOT EMPTY'
  const currentOperatorSet = criteriaTypes[filter.id] || 'regular'
  const currentOperators = operators[currentOperatorSet] || []
  const isDateRange = currentOperatorSet === 'date' && filter.operator === 'IS BETWEEN'

  const handleOpenChange = (type: ActivePopover, open: boolean) => {
    setActivePopover(open ? type : null)
    if (!open) setSearchTerm('')
  }

  const handleCriterionChange = (newCriterion: string) => {
    const updatedFilters = [...currentFilters]
    const newCriterionType = criteriaTypes[newCriterion] || 'regular'
    const currentCriterionType = criteriaTypes[filter.id] || 'regular'

    const newOperatorList = operators[newCriterionType] || []
    const newOperator =
      newCriterionType !== currentCriterionType ? (newOperatorList[0]?.value as Operator) : filter.operator

    updatedFilters[index] = {
      ...filter,
      id: newCriterion,
      operator: newOperator,
      value: [],
    }

    onChange(updatedFilters)
    handleOpenChange(null, false)
  }

  const handleOperatorChange = (newOperator: Operator) => {
    const updatedFilters = [...currentFilters]

    const shouldClearValues = newOperator === 'IS EMPTY' || newOperator === 'IS NOT EMPTY'
    const wasPreviouslyEmpty = filter.operator === 'IS EMPTY' || filter.operator === 'IS NOT EMPTY'
    const isNowValueBased = newOperator === 'IN' || newOperator === 'NOT IN'

    // logic for handling date range to single date transition
    let newValue = filter.value

    // if we are in a date context and the new operator is not "is between"
    // but we have multiple values, keep only the first one
    if (currentOperatorSet === 'date' && newOperator !== 'IS BETWEEN' && Array.isArray(newValue)) {
      newValue = newValue.slice(0, 1)
    }

    updatedFilters[index] = {
      ...filter,
      operator: newOperator,
      value: shouldClearValues ? undefined : wasPreviouslyEmpty && isNowValueBased ? [] : newValue,
    }

    onChange(updatedFilters)
    handleOpenChange(null, false)
  }

  const dateSelectHandler = (timestamps: number[], isStartDate?: boolean) => {
    const updatedFilters = [...currentFilters]
    if (isDateRange && isStartDate !== undefined) {
      const currentValues = (filter.value || []) as number[]
      const newValue = [...currentValues]

      if (isStartDate) {
        newValue[0] = timestamps[0]
      } else {
        newValue[1] = timestamps[0]
      }

      updatedFilters[index] = {...filter, value: newValue}
    } else {
      updatedFilters[index] = {...filter, value: timestamps}
    }
    onChange(updatedFilters)
    handleOpenChange(null, false)
  }

  const getOperatorLabel = () => {
    const operator = currentOperators.find(op => op.value === filter.operator)
    return operator?.label ? t(operator.label) : filter.operator
  }

  const getValueLabel = () => {
    if (isValueSelectorDisabled) return '—'
    if (!filter.value) return ''
    if (filter.value.length === 0) return `(${t(currentOperatorSet === 'date' ? 'select_date' : 'select_value')})`
    if (filter.value.length === 1) {
      if (currentOperatorSet === 'regular') {
        const option = filterOptions[filter.id]?.find(opt => opt.value === filter?.value?.[0])
        return option?.label || filter.value[0]
      }
      if (currentOperatorSet === 'date') return getLongDate(filter.value[0] as number)
    }
    return `${filter.value.length} ${t('items_selected')}`
  }

  const filteredCriteria = Object.keys(criteriaTypes).filter(c => c.toLowerCase().includes(searchTerm.toLowerCase()))
  const filteredOptions =
    filterOptions[filter.id]?.filter(
      opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.value.toLowerCase().includes(searchTerm.toLowerCase())
    ) || []
  const filteredOperators = currentOperators?.filter(op => t(op.label).toLowerCase().includes(searchTerm.toLowerCase()))

  return (
    <Flex align="center" bg="gray.subtle" color="gray.fg">
      {/* CRITERION */}
      <PopoverRoot
        open={activePopover === 'criterion'}
        onOpenChange={e => handleOpenChange('criterion', e.open)}
        lazyMount
        unmountOnExit
      >
        <PopoverTrigger asChild>
          <Button size="sm" variant="surface" borderEndRadius="none">
            {getCriterionIcon(filter.id, optionIcons)}
            {t(`filter.field.${filter.id}`)}
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverPositioner>
            <PopoverContent minWidth={200} maxWidth={250}>
              <ListSearchInput placeholder={`${t('search')}...`} value={searchTerm} onChange={setSearchTerm} />
              <FilterListbox
                items={filteredCriteria.map(c => ({
                  label: t(`filter.field.${c}`),
                  value: c,
                  icon: getCriterionIcon(c, optionIcons),
                }))}
                value={filter.id}
                onValueChange={handleCriterionChange}
              />
            </PopoverContent>
          </PopoverPositioner>
        </Portal>
      </PopoverRoot>

      {/* OPERATOR */}
      <PopoverRoot
        open={activePopover === 'operator'}
        onOpenChange={e => handleOpenChange('operator', e.open)}
        lazyMount
        unmountOnExit
      >
        <PopoverTrigger asChild>
          <Button size="sm" variant="surface" borderRadius="none">
            {getOperatorLabel()}
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverPositioner>
            <PopoverContent minWidth={150} maxWidth={200}>
              <ListSearchInput placeholder={`${t('search')}...`} value={searchTerm} onChange={setSearchTerm} />
              <FilterListbox
                items={filteredOperators.map(op => ({label: t(op.label), value: op.value as Operator}))}
                value={filter.operator as Operator}
                onValueChange={handleOperatorChange}
              />
            </PopoverContent>
          </PopoverPositioner>
        </Portal>
      </PopoverRoot>

      {isDateRange ? (
        <>
          {/* START DATE */}
          <PopoverRoot
            open={activePopover === 'startDate'}
            onOpenChange={e => handleOpenChange('startDate', e.open)}
            lazyMount
            unmountOnExit
          >
            <PopoverTrigger asChild>
              <Button size="sm" variant="surface" minWidth={16} borderRadius="none">
                {filter.value?.[0] && <LuCalendar size={16} />}
                {filter.value?.[0] ? getLongDate(filter.value[0] as number) : `(${t('select_date')})`}
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverPositioner>
                <PopoverContent minWidth={420} maxWidth={420}>
                  <DatePickerSelector
                    onChange={ts => dateSelectHandler(ts, true)}
                    rangeSelect={false}
                    currentValue={filter.value?.[0] ? [filter.value[0] as number] : undefined}
                    open={activePopover === 'startDate'}
                    maxDate={filter.value?.[1] as number}
                  />
                </PopoverContent>
              </PopoverPositioner>
            </Portal>
          </PopoverRoot>

          <Flex align="center" px={4} bg="gray.subtle" borderY="1px solid" borderColor="inherit" height="full">
            <Text fontSize="sm" fontWeight="medium">
              {t('and')}
            </Text>
          </Flex>

          {/* END DATE */}
          <PopoverRoot
            open={activePopover === 'endDate'}
            onOpenChange={e => handleOpenChange('endDate', e.open)}
            lazyMount
            unmountOnExit
          >
            <PopoverTrigger asChild>
              <Button size="sm" variant="surface" minWidth={16} borderRadius="none">
                {filter.value?.[1] && <LuCalendar size={16} />}
                {filter.value?.[1] ? getLongDate(filter.value[1] as number) : `(${t('select_date')})`}
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverPositioner>
                <PopoverContent minWidth={420} maxWidth={420}>
                  <DatePickerSelector
                    onChange={ts => dateSelectHandler(ts, false)}
                    rangeSelect={false}
                    currentValue={filter.value?.[1] ? [filter.value[1] as number] : undefined}
                    open={activePopover === 'endDate'}
                    minDate={filter.value?.[0] as number}
                  />
                </PopoverContent>
              </PopoverPositioner>
            </Portal>
          </PopoverRoot>
        </>
      ) : (
        /* SINGLE VALUE / REGULAR VALUE */
        <PopoverRoot
          open={activePopover === 'value'}
          onOpenChange={e => !isValueSelectorDisabled && handleOpenChange('value', e.open)}
          lazyMount
          unmountOnExit
        >
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="surface"
              minWidth={16}
              borderRadius="none"
              disabled={isValueSelectorDisabled}
              opacity={isValueSelectorDisabled ? 0.5 : 1}
            >
              {currentOperatorSet === 'date' && filter.value?.[0] && <LuCalendar size={16} />}
              {getValueLabel()}
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverPositioner>
              <PopoverContent
                minWidth={criteriaTypes?.[filter.id] === 'date' ? 420 : 250}
                maxWidth={criteriaTypes?.[filter.id] === 'date' ? 420 : 300}
              >
                {criteriaTypes?.[filter.id] === 'date' ? (
                  <DatePickerSelector
                    onChange={dateSelectHandler}
                    rangeSelect={false}
                    currentValue={filter.value?.slice(0, 1) as number[]}
                    open={activePopover === 'value'}
                  />
                ) : (
                  <>
                    <ListSearchInput placeholder={`${t('search')}...`} value={searchTerm} onChange={setSearchTerm} />
                    <FilterListbox
                      items={filteredOptions.map(option => ({
                        ...option,
                        icon: getOptionIcon(option.value, filter.id, optionIcons),
                      }))}
                      value={(filter.value || []) as string[]}
                      multiple
                      onValueChange={values => {
                        const updatedFilters = [...currentFilters]
                        updatedFilters[index] = {...filter, value: values}
                        onChange(updatedFilters)
                      }}
                    />
                  </>
                )}
              </PopoverContent>
            </PopoverPositioner>
          </Portal>
        </PopoverRoot>
      )}

      <Button
        size="sm"
        variant="surface"
        borderStartRadius="none"
        onClick={() => onChange(currentFilters.filter((_, i) => i !== index))}
        p={0}
      >
        <LuX size={16} />
      </Button>
    </Flex>
  )
}

function getLongDate(timestamp: number) {
  const date = new Date(timestamp * 1000)
  return date.toLocaleDateString(Tolgee.locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
